import { useRef, useState } from 'react'
import { api, errorText } from '../api/client'
import type { Attachment, Note, Subject } from '../api/types'
import { relativeDays } from '../lib/format'
import { subjectKey } from '../lib/subject'
import { useAsync } from '../lib/useAsync'
import { Icon } from './Icon'
import { useConfirm } from './Confirm'
import { useToast } from './Toast'

const MAX_BYTES = 5_000_000

/** The two or three things to remember about someone, kept out of the timeline so
 *  they don't scroll away. The composer stays shut until you want it: an empty box
 *  is the biggest thing on the panel otherwise, and the notes are the point. */
export function NotesPanel({ subject, onChange }: { subject: Subject; onChange?: () => void }) {
  const [bump, setBump] = useState(0)
  const { data } = useAsync(() => api.notes(subject), [subjectKey(subject), bump])
  const [draft, setDraft] = useState('')
  const [editing, setEditing] = useState<number | null>(null)
  const [editDraft, setEditDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const toast = useToast()
  const confirm = useConfirm()
  const notes = data?.notes ?? []

  async function run(fn: () => Promise<unknown>, done?: string) {
    setBusy(true)
    try {
      await fn()
      if (done) toast(done, { tone: 'positive' })
      setBump((n) => n + 1)
      onChange?.()
      return true
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
      return false
    } finally {
      setBusy(false)
    }
  }

  async function save() {
    if (!draft.trim()) return
    if (await run(() => api.addNote(subject, draft.trim()), 'Note saved')) setDraft('')
  }

  return (
    <div>
      <div>
        <div className="note-compose">
          <textarea className="field note-field" rows={1} value={draft} maxLength={4000}
            placeholder="What should you remember before the next call?"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void save()
              if (e.key === 'Escape') setDraft('')
            }} />
          {draft.trim() ? (
            <div className="note-compose-foot">
              <span className="hint">⌘↵ to save</span>
              <span className="spacer" />
              <button className="btn sm" onClick={() => setDraft('')}>Clear</button>
              <button className="btn sm primary" disabled={busy} onClick={save}>Save note</button>
            </div>
          ) : null}
        </div>

        {notes.map((n: Note) => (
          <div key={n.id} className="note">
            <span className="note-mark"><Icon name="sticky_note_2" size={14} /></span>
            <div style={{ flex: 1, minWidth: 0 }}>
              {editing === n.id ? (
                <>
                  <textarea className="field" rows={3} value={editDraft} maxLength={4000} autoFocus
                    onChange={(e) => setEditDraft(e.target.value)} />
                  <div className="note-compose-foot">
                    <span className="spacer" />
                    <button className="btn sm" onClick={() => setEditing(null)}>Cancel</button>
                    <button className="btn sm primary" disabled={busy || !editDraft.trim()}
                      onClick={() => run(() => api.updateNote(n.id, { body: editDraft.trim() }), 'Note updated').then(() => setEditing(null))}>
                      Save
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="note-body">{n.body}</div>
                  <div className="note-meta">
                    <span>{n.author ?? 'Someone'} · {relativeDays(n.updated_at)}</span>
                    <span className="spacer" />
                    <button className="icon-btn-plain" title="Edit" aria-label="Edit note"
                      onClick={() => { setEditing(n.id); setEditDraft(n.body) }}><Icon name="edit" size={15} /></button>
                    <button className="icon-btn-plain" title="Delete" aria-label="Delete note"
                      onClick={async () => {
                        if (await confirm({ title: 'Delete this note?', confirmLabel: 'Delete', danger: true })) {
                          void run(() => api.deleteNote(n.id), 'Note deleted')
                        }
                      }}>
                      <Icon name="delete" size={15} />
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

const sizeLabel = (n: number) => (n < 1000 ? `${n} B` : n < 1_000_000 ? `${Math.round(n / 1000)} KB` : `${(n / 1_000_000).toFixed(1)} MB`)

const FILE_ICON: Record<string, { icon: string; tone: string }> = {
  'application/pdf': { icon: 'picture_as_pdf', tone: 'var(--danger)' },
  'image/png': { icon: 'image', tone: 'var(--accent)' },
  'image/jpeg': { icon: 'image', tone: 'var(--accent)' },
  'image/heic': { icon: 'image', tone: 'var(--accent)' },
  'image/webp': { icon: 'image', tone: 'var(--accent)' },
  'text/plain': { icon: 'description', tone: 'var(--ink-3)' },
  'text/csv': { icon: 'table_rows', tone: 'var(--positive)' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { icon: 'description', tone: 'var(--accent)' },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { icon: 'table_rows', tone: 'var(--positive)' },
}

/** Files on the record: a pre-approval letter, a rate quote, a signed agreement.
 *  Borrower documents belong in the loan file — see the line under the list. */
export function FilesPanel({ subject, onChange }: { subject: Subject; onChange?: () => void }) {
  const [bump, setBump] = useState(0)
  const { data } = useAsync(() => api.attachments(subject), [subjectKey(subject), bump])
  const [busy, setBusy] = useState(false)
  const [over, setOver] = useState(false)
  const input = useRef<HTMLInputElement>(null)
  const toast = useToast()
  const confirm = useConfirm()
  const files = data?.attachments ?? []

  async function upload(file: File | undefined) {
    if (!file) return
    if (file.size > MAX_BYTES) return toast('Files are up to 5 MB for now.', { tone: 'danger' })
    setBusy(true)
    try {
      const buffer = await file.arrayBuffer()
      let binary = ''
      new Uint8Array(buffer).forEach((b) => { binary += String.fromCharCode(b) })
      await api.addAttachment(subject, {
        filename: file.name,
        content_type: file.type || 'application/octet-stream',
        data_base64: btoa(binary),
      })
      toast(`${file.name} attached`, { tone: 'positive' })
      setBump((n) => n + 1)
      onChange?.()
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={over ? 'drop-target' : undefined}
      onDragOver={(e) => { e.preventDefault(); setOver(true) }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); void upload(e.dataTransfer.files?.[0]) }}>
      <div className="panel-head">
        <span className="quiet">Letters, quotes and agreements. Keep borrower documents in the loan file.</span>
        <span className="spacer" />
        <button className="btn sm" disabled={busy} onClick={() => input.current?.click()}>
          <Icon name="upload_file" size={15} /> {busy ? 'Uploading…' : 'Upload'}
        </button>
        <input ref={input} type="file" hidden disabled={busy}
          accept=".pdf,.png,.jpg,.jpeg,.heic,.webp,.txt,.csv,.docx,.xlsx"
          onChange={(e) => { void upload(e.target.files?.[0]); e.target.value = '' }} />
      </div>
      <div>
        {files.length === 0 ? (
          <button className="note-empty" onClick={() => input.current?.click()}>
            <Icon name="upload_file" size={18} />
            <span>Pre-approval letters, rate quotes, signed agreements. Drop one here — up to 5 MB.</span>
          </button>
        ) : files.map((f: Attachment) => {
          const look = FILE_ICON[f.content_type] ?? { icon: 'attach_file', tone: 'var(--ink-3)' }
          return (
            <div key={f.id} className="filerow">
              <span className="filerow-icon" style={{ color: look.tone }}><Icon name={look.icon} size={17} /></span>
              <a href={api.attachmentUrl(f.id)} download={f.filename} style={{ flex: 1, minWidth: 0 }}>
                <span className="filerow-name">
                  {f.filename}
                  {f.is_card ? <span className="badge t-accent" style={{ marginLeft: 6 }}>Business card</span> : null}
                </span>
                <span className="filerow-meta">{sizeLabel(f.size_bytes)} · {f.uploaded_by ?? 'someone'} · {relativeDays(f.created_at)}</span>
              </a>
              {f.content_type.startsWith('image/') ? (
                <button className="icon-btn-plain" title={f.is_card ? 'Stop showing this on the contact card' : 'Show this on the contact card as their business card'}
                  aria-label={f.is_card ? 'Unset as business card' : 'Use as business card'}
                  onClick={async () => {
                    try {
                      await api.setBusinessCard(f.id, !f.is_card)
                      setBump((n) => n + 1)
                      onChange?.()
                    } catch (e) {
                      toast(errorText(e), { tone: 'danger' })
                    }
                  }}><Icon name={f.is_card ? 'badge' : 'badge'} size={15} style={f.is_card ? { color: 'var(--accent)' } : undefined} /></button>
              ) : null}
              <a className="icon-btn-plain" href={api.attachmentUrl(f.id)} download={f.filename}
                title="Download" aria-label={`Download ${f.filename}`}><Icon name="download" size={15} /></a>
              <button className="icon-btn-plain" title="Remove" aria-label={`Remove ${f.filename}`}
                onClick={async () => {
                  if (!await confirm({ title: `Remove ${f.filename}?`, confirmLabel: 'Remove', danger: true })) return
                  try {
                    await api.deleteAttachment(f.id)
                    setBump((n) => n + 1)
                  } catch (e) {
                    toast(errorText(e), { tone: 'danger' })
                  }
                }}><Icon name="delete" size={15} /></button>
            </div>
          )
        })}

      </div>
    </div>
  )
}
